import React, {
  DetailedHTMLProps,
  Fragment,
  InputHTMLAttributes,
  ReactNode,
  useEffect,
  useState,
} from 'react'
import { Controller, useFormContext } from 'react-hook-form'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCircleExclamation } from '@fortawesome/pro-regular-svg-icons'
import {
  useGoogleServices,
  usePlacesAutocompleteService,
  usePlacesDetailsService,
} from '@/shared/hooks/GoogleServiceQueries'
import { GooglePlacesAutocompleteResult } from '@/lib/apis/types'
import { useDebounce } from '@uidotdev/usehooks'
import { DetailedAddress } from '@/lib/types'
import Spinner from '@/shared/components/Spinner'
import {
  Combobox,
  ComboboxInput,
  ComboboxOption,
  ComboboxOptions,
  Transition,
} from '@headlessui/react'
import clsx from 'clsx'

type QuizGooglePlacesInputProps = {
  name: string
  label: string
  onPickAddress: (address: DetailedAddress) => void
  hasError?: boolean
  contentAtEnd?: ReactNode
} & DetailedHTMLProps<InputHTMLAttributes<HTMLInputElement>, HTMLInputElement>

export default function QuizGooglePlacesInput({
  name,
  type,
  label,
  onPickAddress,
  hasError,
  contentAtEnd,

  ...props
}: QuizGooglePlacesInputProps) {
  const { register, watch, formState, control, setValue } = useFormContext()
  const value = watch(name)
  const field = register(name)
  const { isSubmitting, isSubmitted, errors, touchedFields } = formState
  const touched = touchedFields?.[name] || isSubmitted
  const error = errors[name]
  const isDisabled = isSubmitting || props.disabled

  const services = useGoogleServices()

  const [input, setInput] = useState('')
  const [selectedPlace, setSelectedPlace] =
    useState<GooglePlacesAutocompleteResult>()
  const debouncedInput = useDebounce(input, 200)
  const predictions = usePlacesAutocompleteService(services, debouncedInput)

  const placeDetailsQuery = usePlacesDetailsService(
    services,
    selectedPlace ? selectedPlace.placeId : '',
  )

  useEffect(() => {
    if (
      !placeDetailsQuery.isLoading &&
      selectedPlace &&
      placeDetailsQuery.data
    ) {
      const data = placeDetailsQuery.data
      const suffix = `, ${data.suburb} ${data.state}`
      const formattedAddress = selectedPlace.description.replace(suffix, '')

      onPickAddress({ formattedAddress, ...placeDetailsQuery.data })
      setSelectedPlace(undefined)
    }
  }, [
    placeDetailsQuery.isLoading,
    placeDetailsQuery.data,
    selectedPlace,
    onPickAddress,
  ])

  function handlePickAddress(value: GooglePlacesAutocompleteResult | null) {
    if (value) {
      setValue(name, value.description, {
        shouldValidate: true,
        shouldDirty: true,
      })
      setInput(value.description)
      setSelectedPlace(value)
    }
  }

  let addressesDropdown
  const lookupUnavailable =
    services.isError || predictions.isError || placeDetailsQuery.isError

  if (lookupUnavailable) {
    addressesDropdown = (
      <div className="px-4 py-2 text-gray-600">
        Enter your address manually below.
      </div>
    )
  } else if (predictions.isFetching) {
    addressesDropdown = (
      <div className="relative cursor-default select-none px-4 py-2 text-gray-500">
        <div className="flex w-full justify-center">
          <Spinner />
        </div>
      </div>
    )
  } else if (!predictions.data || predictions.data.length === 0) {
    addressesDropdown = (
      <div className="relative cursor-default select-none px-4 py-2 text-gray-500">
        No address found.
      </div>
    )
  } else {
    addressesDropdown = (
      <>
        {predictions.data?.map((prediction) => (
          <ComboboxOption
            key={prediction.placeId}
            value={prediction}
            className={({ focus }) =>
              `relative cursor-pointer select-none py-2.5 pl-2.5 pr-4 sm:leading-6 ${
                focus ? 'bg-blue-600 text-white' : 'text-gray-700'
              }`
            }
          >
            {prediction.description}
          </ComboboxOption>
        ))}
      </>
    )
  }

  return (
    <div>
      <div className="relative flex items-center">
        <Combobox onChange={handlePickAddress} disabled={isDisabled}>
          <Controller
            name={name}
            control={control}
            render={({ field }) => (
              <ComboboxInput
                {...props}
                type="text"
                {...field}
                disabled={isDisabled}
                aria-invalid={Boolean((touched && error) || hasError)}
                aria-describedby={
                  touched && error ? `${name}-error` : undefined
                }
                onChange={(e) => {
                  setSelectedPlace(undefined)
                  field.onChange(e)
                  setInput(e.target.value)
                }}
                className={clsx(
                  'peer block h-[4.5rem] w-full rounded-2xl border-0 px-4 pb-0 text-base text-gray-900 ring-2 ring-inset focus:ring-2 focus:ring-inset focus:ring-blue-600 sm:leading-6',
                  (touched && error) || hasError
                    ? 'ring-red-600'
                    : 'ring-gray-300',
                  isDisabled ? 'bg-gray-300' : '',
                )}
                id={name}
                name={field.name}
              />
            )}
          />
          {debouncedInput.length >= 3 && (
            <Transition
              as={Fragment}
              leave="transition ease-in duration-100"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <ComboboxOptions
                modal={false}
                className="absolute top-[4.6rem] z-50 mt-1 max-h-60 w-full overflow-auto rounded-2xl bg-white py-2.5 text-base shadow-lg ring-2 ring-black ring-opacity-5 focus:outline-none"
              >
                {addressesDropdown}
              </ComboboxOptions>
            </Transition>
          )}
        </Combobox>
        <label
          htmlFor={name}
          className={clsx(
            'absolute origin-top-left pl-4 text-base text-gray-400 transition-all duration-300',

            // If the field is not empty, leave the label up top
            value
              ? '-translate-y-3 translate-x-1 scale-75'
              : 'peer-focus:-translate-y-3 peer-focus:translate-x-1 peer-focus:scale-75',
          )}
        >
          {label}
        </label>
      </div>

      {lookupUnavailable && (
        <p role="status" className="mt-2 text-sm text-gray-600">
          Address lookup is unavailable. Enter your street address, suburb,
          state and postcode manually.
        </p>
      )}
      {touched && error && (
        <div
          id={`${name}-error`}
          className="relative mt-1 block pl-4 text-sm text-red-600"
        >
          <FontAwesomeIcon icon={faCircleExclamation} className="mr-1.5" />
          {`${error.message}`}
        </div>
      )}
    </div>
  )
}
